// The QC gate refused an approval (F03 UF-05): say so as ONE choice — open the
// failed check, or close — instead of a dead-end alert. The card's own hold
// names the check when it has one; a pallet the card could not flag (fruit
// from a build-up line) is looked up on refusal. Where no check can be found,
// or the viewer may not open QC, it is today's "Approval blocked" alert.
import { useCallback } from 'react'
import { alertDialog, choiceDialog } from '@/components/Dialogs'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useNavigateWithReturn } from '@/lib/returnTo'
import { qcCheckHref } from '@/lib/qcCheckLink'
import { qcHoldRefusal } from '@/lib/packhouseErrorDomains'
import { blockingCheckFor, type QcHold } from './inspectionReads'

export function useExplainQcRefusal(hold: QcHold | undefined, canOpenQc = false) {
  const tenantId = useActiveTenantId()
  const navigateWithReturn = useNavigateWithReturn()
  return useCallback(async (pallet: { pallet_id: string }, e: Error) => {
    let check: QcHold | null = hold ?? null
    if (!check && canOpenQc && tenantId) {
      // Best effort: a failed lookup still gets the refusal, as an alert.
      check = await blockingCheckFor(tenantId, pallet.pallet_id).catch(() => null)
    }
    // The gate's own text says "disposition" and names the pallet id: the
    // operator reads the plain sentence (QC_HOLD_DOMAIN).
    const message = qcHoldRefusal(e) ?? e.message
    if (!check || !canOpenQc) {
      await alertDialog({ title: 'Approval blocked', message, tone: 'danger' })
      return
    }
    const pick = await choiceDialog({
      title: 'Approval blocked', message, tone: 'danger', cancel: 'Close',
      options: [{ value: 'open', label: 'Open the failed check' }],
    })
    if (pick === 'open') navigateWithReturn(qcCheckHref(check))
  }, [hold, canOpenQc, tenantId, navigateWithReturn])
}
